import type {
  WindowPartitionJson,
  WindowPartitionState,
  WindowStatusJson,
} from "@/types/api";
import { formatNumber } from "@/utils/format";

// What the Metrics card shows for a windowed pipeline. Its records are not
// counted one by one as they pass, so events, bytes, throughput and lag do
// not apply; what does is what the shared store says the windows settled.
//
// A total is a sum over the partitions, and a sum over some of them is no
// total: it is shown only when every partition the plan names was read, each
// once. The partition badge never reassures over a read that was not whole.

export type BadgeTone = "confirmation" | "warning" | "error";

export interface WindowMetricCell {
  label: string;
  value: string;
  sub?: string;
  badge?: { tone: BadgeTone; text: string };
}

export interface WindowMetricsDisplay {
  cells: WindowMetricCell[];
  notes: string[];
}

type Totals = NonNullable<WindowPartitionJson["durable"]>["totals"];

const STATE_ORDER: WindowPartitionState[] = [
  "idle",
  "busy",
  "retry",
  "failed",
  "unassigned",
  "unknown",
];

const NO_VALUE = "—";

/** States that are not failures yet still want someone to look. */
const ATTENTION_STATES: WindowPartitionState[] = [
  "retry",
  "unassigned",
  "unknown",
];

/** Whether the partitions are exactly the plan's, 0 to n-1 each once, and
 *  every one durably read. */
function coversThePlan(status: WindowStatusJson): boolean {
  const expected = status.plan?.partitions;
  if (expected === undefined || status.partitions.length !== expected) {
    return false;
  }
  const seen = new Set(status.partitions.map((p) => p.partition));
  const inRange = [...seen].every((p) => p >= 0 && p < expected);
  const durable = status.partitions.every((p) => p.durable !== null);
  return seen.size === expected && inRange && durable;
}

function sumOf(status: WindowStatusJson, pick: (d: Totals) => number): number {
  return status.partitions.reduce(
    (sum, p) => sum + (p.durable ? pick(p.durable.totals) : 0),
    0,
  );
}

function totalCells(
  status: WindowStatusJson,
  complete: boolean,
): WindowMetricCell[] {
  const total = (label: string, value: () => number): WindowMetricCell =>
    complete
      ? { label, value: formatNumber(value()) }
      : { label, value: NO_VALUE, sub: "partial read" };
  return [
    total("Windows settled", () =>
      sumOf(status, (t) => t.settled_group_windows),
    ),
    total("Output records", () =>
      sumOf(status, (t) => t.settled_output_records),
    ),
    total("Sink deliveries", () =>
      sumOf(status, (t) => t.settled_sink_deliveries),
    ),
    total("Open windows", () =>
      status.partitions.reduce((s, p) => s + (p.durable?.open_windows ?? 0), 0),
    ),
  ];
}

function stateCounts(
  status: WindowStatusJson,
): Map<WindowPartitionState, number> {
  const counts = new Map<WindowPartitionState, number>();
  for (const p of status.partitions) {
    counts.set(p.state, (counts.get(p.state) ?? 0) + 1);
  }
  return counts;
}

function statesText(counts: Map<WindowPartitionState, number>): string {
  const parts = STATE_ORDER.flatMap((state) => {
    const n = counts.get(state) ?? 0;
    return n > 0 ? [`${n} ${state}`] : [];
  });
  return parts.length > 0 ? parts.join(", ") : NO_VALUE;
}

/** The partition badge: the first that applies, so nothing reassures over a
 *  read that was not whole. */
function partitionBadge(
  status: WindowStatusJson,
  counts: Map<WindowPartitionState, number>,
  complete: boolean,
): { tone: BadgeTone; text: string } {
  const some = (state: WindowPartitionState) => (counts.get(state) ?? 0) > 0;
  if (status.read.status === "unavailable") {
    return { tone: "warning", text: "store unavailable" };
  }
  if (some("failed")) return { tone: "error", text: "failed" };
  if (status.read.status === "partial" || !complete) {
    return { tone: "warning", text: "partial read" };
  }
  if (ATTENTION_STATES.some(some)) {
    return { tone: "warning", text: "attention" };
  }
  return { tone: "confirmation", text: "healthy" };
}

function blockedNotes(status: WindowStatusJson): string[] {
  return [...status.partitions]
    .sort((a, b) => a.partition - b.partition)
    .flatMap((p) => {
      const d = p.diagnostic;
      if (!d?.confirmed || d.blocked == null) return [];
      return [
        `partition ${p.partition} blocked: ${d.cause?.kind ?? "unknown"}`,
      ];
    });
}

export function windowMetricsDisplay(
  status: WindowStatusJson,
): WindowMetricsDisplay {
  const unavailable = status.read.status === "unavailable";
  const complete = status.read.status === "complete" && coversThePlan(status);
  const counts = stateCounts(status);
  const partitions: WindowMetricCell = {
    label: "Partitions",
    value: unavailable ? NO_VALUE : statesText(counts),
    badge: partitionBadge(status, counts, complete),
  };
  return {
    cells: [...totalCells(status, complete), partitions],
    notes: blockedNotes(status),
  };
}
